/**
 * Mock notifications data for development.
 * Covers all 9 notification types, with timestamps spread across
 * Today, This Week, and Earlier sections.
 */
import { subHours, subDays, subMinutes } from 'date-fns';
import { MOCK_USERS, MOCK_CURRENT_USER_ID } from './mock-users.data';

const now = new Date();
const u = (id) => MOCK_USERS.find((x) => x.id === id) || MOCK_USERS[1];

const makeNotif = (overrides) => ({
  id: overrides.id || `mock_notif_${Date.now().toString(36)}`,
  recipientId: MOCK_CURRENT_USER_ID,
  senderId: '',
  senderName: '',
  senderUsername: '',
  senderAvatarUrl: null,
  type: 'like',
  postId: null,
  read: false,
  message: '',
  metadata: null,
  createdAt: new Date().toISOString(),
  _docSnapshot: null,
  ...overrides,
});

export const MOCK_NOTIFICATIONS = [
  // ── TODAY ──────────────────────────────────────────────────────────────────
  // 3 people liked the same post (for grouping)
  makeNotif({
    id: 'mock_notif_01',
    senderId: 'mock_user_003',
    senderName: u('mock_user_003').displayName,
    senderUsername: u('mock_user_003').username,
    senderAvatarUrl: u('mock_user_003').avatarUrl,
    type: 'like',
    postId: 'mock_post_t01',
    message: 'Marcus Johnson liked your post.',
    createdAt: subMinutes(now, 15).toISOString(),
  }),
  makeNotif({
    id: 'mock_notif_02',
    senderId: 'mock_user_004',
    senderName: u('mock_user_004').displayName,
    senderUsername: u('mock_user_004').username,
    senderAvatarUrl: u('mock_user_004').avatarUrl,
    type: 'like',
    postId: 'mock_post_t01',
    message: 'Priya Patel liked your post.',
    createdAt: subMinutes(now, 18).toISOString(),
  }),
  makeNotif({
    id: 'mock_notif_03',
    senderId: 'mock_user_006',
    senderName: u('mock_user_006').displayName,
    senderUsername: u('mock_user_006').username,
    senderAvatarUrl: u('mock_user_006').avatarUrl,
    type: 'like',
    postId: 'mock_post_t01',
    message: 'Elena Rodriguez liked your post.',
    createdAt: subMinutes(now, 20).toISOString(),
  }),
  makeNotif({
    id: 'mock_notif_04',
    senderId: 'mock_user_002',
    senderName: u('mock_user_002').displayName,
    senderUsername: u('mock_user_002').username,
    senderAvatarUrl: u('mock_user_002').avatarUrl,
    type: 'comment',
    postId: 'mock_post_t01',
    message: 'Sarah Chen commented on your post: "That\'s incredible! What program are you following?"',
    createdAt: subMinutes(now, 45).toISOString(),
  }),
  makeNotif({
    id: 'mock_notif_05',
    senderId: 'mock_user_010',
    senderName: u('mock_user_010').displayName,
    senderUsername: u('mock_user_010').username,
    senderAvatarUrl: u('mock_user_010').avatarUrl,
    type: 'follow',
    postId: null,
    message: 'Jasmine Okafor started following you.',
    createdAt: subHours(now, 2).toISOString(),
  }),
  makeNotif({
    id: 'mock_notif_06',
    senderId: 'mock_user_002',
    senderName: u('mock_user_002').displayName,
    senderUsername: u('mock_user_002').username,
    senderAvatarUrl: u('mock_user_002').avatarUrl,
    type: 'partner_checkin_reminder',
    postId: null,
    message: 'Don\'t forget to check in with Sarah Chen today!',
    createdAt: subHours(now, 5).toISOString(),
    read: true,
  }),
  makeNotif({
    id: 'mock_notif_07',
    senderId: MOCK_CURRENT_USER_ID,
    senderName: 'FitSocial',
    senderUsername: 'fitsocial',
    senderAvatarUrl: null,
    type: 'streak_milestone',
    postId: null,
    message: 'You\'re on a 12-day streak! Keep it going!',
    metadata: { streakCount: 12 },
    createdAt: subHours(now, 8).toISOString(),
  }),

  // ── THIS WEEK ──────────────────────────────────────────────────────────────
  makeNotif({
    id: 'mock_notif_08',
    senderId: 'mock_user_005',
    senderName: u('mock_user_005').displayName,
    senderUsername: u('mock_user_005').username,
    senderAvatarUrl: u('mock_user_005').avatarUrl,
    type: 'like',
    postId: 'mock_post_w01',
    message: 'Jake Thompson liked your workout post.',
    createdAt: subDays(now, 1).toISOString(),
    read: true,
  }),
  makeNotif({
    id: 'mock_notif_09',
    senderId: 'mock_user_007',
    senderName: u('mock_user_007').displayName,
    senderUsername: u('mock_user_007').username,
    senderAvatarUrl: u('mock_user_007').avatarUrl,
    type: 'comment',
    postId: 'mock_post_w01',
    message: 'David Kim commented on your post: "Great push session! Mind sharing your OHP programming?"',
    createdAt: subDays(now, 1).toISOString(),
    read: true,
  }),
  makeNotif({
    id: 'mock_notif_10',
    senderId: 'mock_user_009',
    senderName: u('mock_user_009').displayName,
    senderUsername: u('mock_user_009').username,
    senderAvatarUrl: u('mock_user_009').avatarUrl,
    type: 'follow',
    postId: null,
    message: 'Ryan Mitchell started following you.',
    createdAt: subDays(now, 2).toISOString(),
    read: true,
  }),
  makeNotif({
    id: 'mock_notif_11',
    senderId: MOCK_CURRENT_USER_ID,
    senderName: 'FitSocial',
    senderUsername: 'fitsocial',
    senderAvatarUrl: null,
    type: 'achievement_unlocked',
    postId: null,
    message: 'Achievement unlocked: Week Warrior (Bronze) - 7-day workout streak!',
    metadata: { achievementType: 'week_warrior_bronze', tier: 'bronze' },
    createdAt: subDays(now, 3).toISOString(),
    read: true,
  }),
  makeNotif({
    id: 'mock_notif_12',
    senderId: 'mock_user_003',
    senderName: u('mock_user_003').displayName,
    senderUsername: u('mock_user_003').username,
    senderAvatarUrl: u('mock_user_003').avatarUrl,
    type: 'workout_shared',
    postId: 'mock_post_w02',
    message: 'Marcus Johnson shared a new workout: Leg Day - Quads & Glutes',
    createdAt: subDays(now, 3).toISOString(),
    read: true,
  }),

  // ── EARLIER ────────────────────────────────────────────────────────────────
  makeNotif({
    id: 'mock_notif_13',
    senderId: 'mock_user_008',
    senderName: u('mock_user_008').displayName,
    senderUsername: u('mock_user_008').username,
    senderAvatarUrl: u('mock_user_008').avatarUrl,
    type: 'follow',
    postId: null,
    message: 'Olivia Foster started following you.',
    createdAt: subDays(now, 8).toISOString(),
    read: true,
  }),
  makeNotif({
    id: 'mock_notif_14',
    senderId: MOCK_CURRENT_USER_ID,
    senderName: 'FitSocial',
    senderUsername: 'fitsocial',
    senderAvatarUrl: null,
    type: 'goal_completed',
    postId: null,
    message: 'Congratulations! You completed your goal: "Bench Press 200lbs"',
    metadata: { goalTitle: 'Bench Press 200lbs' },
    createdAt: subDays(now, 10).toISOString(),
    read: true,
  }),
  makeNotif({
    id: 'mock_notif_15',
    senderId: 'mock_user_002',
    senderName: u('mock_user_002').displayName,
    senderUsername: u('mock_user_002').username,
    senderAvatarUrl: u('mock_user_002').avatarUrl,
    type: 'partner_request',
    postId: null,
    message: 'Sarah Chen wants to be your accountability partner!',
    createdAt: subDays(now, 12).toISOString(),
    read: true,
  }),
  makeNotif({
    id: 'mock_notif_16',
    senderId: 'mock_user_005',
    senderName: u('mock_user_005').displayName,
    senderUsername: u('mock_user_005').username,
    senderAvatarUrl: u('mock_user_005').avatarUrl,
    type: 'like',
    postId: 'mock_post_p01',
    message: 'Jake Thompson liked your progress photo.',
    createdAt: subDays(now, 9).toISOString(),
    read: true,
  }),
  makeNotif({
    id: 'mock_notif_17',
    senderId: 'mock_user_006',
    senderName: u('mock_user_006').displayName,
    senderUsername: u('mock_user_006').username,
    senderAvatarUrl: u('mock_user_006').avatarUrl,
    type: 'comment',
    postId: 'mock_post_p01',
    message: 'Elena Rodriguez commented: "Amazing transformation! You\'re inspiring!"',
    createdAt: subDays(now, 9).toISOString(),
    read: true,
  }),
  makeNotif({
    id: 'mock_notif_18',
    senderId: MOCK_CURRENT_USER_ID,
    senderName: 'FitSocial',
    senderUsername: 'fitsocial',
    senderAvatarUrl: null,
    type: 'streak_milestone',
    postId: null,
    message: 'You hit a 7-day streak! Bronze tier unlocked!',
    metadata: { streakCount: 7 },
    createdAt: subDays(now, 14).toISOString(),
    read: true,
  }),
];
